import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { SlotMachineAchievementViewModel } from '../../../../aida/viewModels/slotMachines';
import { IModalContext, ModalChildComponentContextKey } from '../../../../models';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { AccountUserViewModel, AdminCoffeeSlotMachineViewModel } from '../../../../viewmodels/AdminViewModels';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../../web/components/Modal';
import { baseStyleSheet } from '../../../../web/styles/styles';

interface IProps extends IModalContext {
	className?: string;
	styles?: StyleDeclarationValue[];
	user?: AccountUserViewModel;
}

const GrantSlotMachineSpinModalBase: React.FC<IProps> = observer(props => {
	const { className, styles = [], user, parentModal } = props;
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const slotMachines = React.useRef<AdminCoffeeSlotMachineViewModel>(
		new AdminCoffeeSlotMachineViewModel(userSession).impersonate({ account: { id: user?.accountId }, user })
	).current;

	const [selectedAchievement, setSelectedAchievement] = React.useState<SlotMachineAchievementViewModel>(null);
	const [count, setCount] = React.useState<number>(1);
	const [busy, setBusy] = React.useState<boolean>(false);

	React.useEffect(() => {
		(async () => {
			try {
				setBusy(true);
				await slotMachines.load();
				setBusy(false);
			} catch (error) {
				setBusy(false);
				errorMessages.pushApiError(error);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slotMachines]);

	const achievmentOptions = React.useMemo<SlotMachineAchievementViewModel[]>(() => {
		return (slotMachines?.achievments || []).filter(x => x.gameId && x.configuration?.enabled);
	}, [slotMachines?.achievments]);

	const grantSpins = async () => {
		try {
			setBusy(true);

			await slotMachines.grantSlotMachineSpin(user, selectedAchievement, count);
			setBusy(false);
			parentModal?.onRequestClose();
		} catch (error) {
			setBusy(false);
			errorMessages.pushApiError(error);
		}
	};

	const onAchievementValueChanged = (value: string) => {
		setSelectedAchievement(achievmentOptions.find(x => x.gameId === value) ?? null);
	};

	const onCountValueChanged = (value: string) => {
		setCount(parseInt(value, 10));
	};

	return (
		<div className={`${css(baseStyleSheet.verticalStack, ...styles)} grant-slot-machine-spin-modal ${className || ''}`}>
			<h4>Grant a spin (for testing)</h4>
			<div>
				<div className={css(baseStyleSheet.tableColumnHeader)}>User:</div>
				<div className={css(baseStyleSheet.horizontalStack)}>
					<span title={user?.email}>{user?.name || ''}</span>
				</div>
			</div>
			<div>
				<div className={css(baseStyleSheet.tableColumnHeader)}>Achievement:</div>
				<Select value={selectedAchievement?.gameId ?? ''} onValueChange={onAchievementValueChanged}>
					<SelectTrigger>
						<SelectValue>
							{(value: string) =>
								value ? (achievmentOptions.find(x => x.gameId === value)?.description ?? value) : 'Please select...'
							}
						</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{achievmentOptions.map(x => (
							<SelectItem key={x.gameId} value={x.gameId}>
								{x.description}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div>
				<div className={css(baseStyleSheet.tableColumnHeader)}>Count:</div>
				<Select value={String(count)} onValueChange={onCountValueChanged}>
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{Array.from({ length: 5 }, (_, i) => String(i + 1)).map(n => (
							<SelectItem key={n} value={n}>
								{n}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div className={css(baseStyleSheet.horizontalStack)}>
				<button
					className={css(baseStyleSheet.ctaButton)}
					disabled={!user || slotMachines.isBusy || !selectedAchievement || busy}
					onClick={grantSpins}
				>
					<span>Ok</span>
				</button>
				{busy ? <LoadingSpinner type='tiny' /> : null}
			</div>
		</div>
	);
});

const GrantSlotMachineSpinModalWithContext = inject(ModalChildComponentContextKey)(GrantSlotMachineSpinModalBase);

export const GrantSlotMachineSpinModal = asModalComponent(GrantSlotMachineSpinModalWithContext, {
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
